import { CodeImageTheme } from '../../data/theme/code-image-themes.data';

/**
 * The Code Image tool's picture, drawn on a canvas: the code in a theme's editor colours on a
 * rounded editor surface, over a backdrop. Pure drawing, no Angular and no Prism here: the page
 * tokenizes the code (Prism) and hands the lines over, so the same function draws the preview and
 * the download, and what is on the page is what is saved.
 */

/** One run of text in one syntax role ('' is plain text). */
export interface CodeSpan {
  text: string;
  role: string;
}

export type CodeBackdrop = 'gradient' | 'solid' | 'none';

export interface CodeImageOptions {
  theme: CodeImageTheme;
  /** the code, one array of spans a line */
  lines: CodeSpan[][];
  /** the space around the editor, in CSS pixels */
  padding: number;
  fontSize: number;
  lineNumbers: boolean;
  backdrop: CodeBackdrop;
  /** the bar above the code with three dots and the title; '' draws the bar without words */
  windowBar: boolean;
  title: string;
  /** device pixels a CSS pixel: 1 on the page, 2 or 3 for a download */
  scale: number;
  /** the preview only: the picture at least this tall (CSS px), the editor centred in the extra backdrop, so an
   *  empty or short snippet still fills the page's preview well (owner 2026-10-13); a download leaves it out */
  minHeight?: number;
}

export const CODE_FONT = '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, "Liberation Mono", monospace';

// A Prism token type to one of the theme's roles; a type that is not here is plain text.
const ROLE_OF: { [ type: string ]: string } = {
  comment: 'comment', prolog: 'comment', doctype: 'comment', cdata: 'comment',
  keyword: 'keyword', atrule: 'keyword', important: 'keyword', boolean: 'keyword', builtin: 'keyword', rule: 'keyword',
  string: 'string', char: 'string', 'attr-value': 'string', regex: 'string', 'template-string': 'string', url: 'string',
  number: 'number', unit: 'number', hexcode: 'number',
  function: 'fn', 'function-variable': 'fn',
  'class-name': 'type', 'maybe-class-name': 'type', namespace: 'type',
  property: 'field', key: 'field',
  parameter: 'param',
  variable: 'variable',
  constant: 'constant', symbol: 'constant',
  tag: 'tag', selector: 'tag',
  'attr-name': 'attr',
  decorator: 'meta', annotation: 'meta', directive: 'meta',
  operator: 'punctuation', punctuation: 'punctuation', entity: 'punctuation',
};

/** The role a Prism token type paints in ('' for plain text). */
export function roleOf( type: string ): string {
  return ROLE_OF[ type ] || '';
}

/**
 * Prism's token tree as lines of spans. A nested token takes its own role when it has one and its
 * parent's otherwise (the quotes inside a string stay string coloured, the name inside a tag is
 * the tag's). Tabs become two spaces.
 */
export function toLines( tokens: any[] ): CodeSpan[][] {
  const lines: CodeSpan[][] = [ [] ];
  const push = ( text: string, role: string ) => {
    const parts = text.replace( /\t/g, '  ' ).replace( /\r/g, '' ).split( '\n' );
    parts.forEach( ( part, i ) => {
      if ( i > 0 ) { lines.push( [] ); }
      if ( part ) { lines[ lines.length - 1 ].push( { text: part, role } ); }
    } );
  };
  const walk = ( token: any, inherited: string ) => {
    if ( typeof token === 'string' ) {
      push( token, inherited );
      return;
    }
    const own = roleOf( token.type ) || roleOf( Array.isArray( token.alias ) ? token.alias[ 0 ] : token.alias || '' );
    // punctuation inside a string or a comment is part of it
    const role = ( own === 'punctuation' && ( inherited === 'string' || inherited === 'comment' ) ) ? inherited : ( own || inherited );
    if ( typeof token.content === 'string' ) {
      push( token.content, role );
    } else if ( Array.isArray( token.content ) ) {
      token.content.forEach( ( child: any ) => walk( child, role ) );
    } else if ( token.content ) {
      walk( token.content, role );
    }
  };
  tokens.forEach( t => walk( t, '' ) );
  return lines;
}

/** two colours half and half, as the middle of a gradient between them */
function mix( a: string, b: string ): string {
  const x = parseInt( a.replace( '#', '' ), 16 );
  const y = parseInt( b.replace( '#', '' ), 16 );
  const channel = ( shift: number ) => Math.round( ( ( ( x >> shift ) & 255 ) + ( ( y >> shift ) & 255 ) ) / 2 );
  return `rgb(${ channel( 16 ) }, ${ channel( 8 ) }, ${ channel( 0 ) })`;
}

function alpha( hex: string, a: number ): string {
  const n = parseInt( hex.replace( '#', '' ), 16 );
  return `rgba(${ ( n >> 16 ) & 255 }, ${ ( n >> 8 ) & 255 }, ${ n & 255 }, ${ a })`;
}

function roundRect( ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number ): void {
  r = Math.max( 0, Math.min( r, w / 2, h / 2 ) );
  ctx.beginPath();
  ctx.moveTo( x + r, y );
  ctx.arcTo( x + w, y, x + w, y + h, r );
  ctx.arcTo( x + w, y + h, x, y + h, r );
  ctx.arcTo( x, y + h, x, y, r );
  ctx.arcTo( x, y, x + w, y, r );
  ctx.closePath();
}

/** The picture's size in CSS pixels for these options (the canvas is this times the scale). */
export function measureCodeImage( ctx: CanvasRenderingContext2D, o: CodeImageOptions ): { width: number; height: number; charWidth: number; gutter: number; lineHeight: number; bar: number } {
  ctx.font = `${ o.fontSize }px ${ CODE_FONT }`;
  const charWidth = ctx.measureText( 'MMMMMMMMMM' ).width / 10;
  const lineHeight = Math.round( o.fontSize * 1.55 );
  const longest = o.lines.reduce( ( m, line ) => Math.max( m, line.reduce( ( n, s ) => n + s.text.length, 0 ) ), 0 );
  const gutter = o.lineNumbers ? Math.ceil( ( String( o.lines.length ).length + 2 ) * charWidth ) : 0;
  const bar = o.windowBar ? 40 : 0;
  const inner = 22;
  // a short snippet still gets a card wide enough to read as an editor
  const cardWidth = Math.max( 320, Math.ceil( gutter + longest * charWidth ) + inner * 2 );
  const cardHeight = bar + o.lines.length * lineHeight + inner * 2 - ( o.windowBar ? 8 : 0 );
  return { width: cardWidth + o.padding * 2, height: cardHeight + o.padding * 2, charWidth, gutter, lineHeight, bar };
}

/** Sizes the canvas and draws the picture on it. */
export function paintCodeImage( canvas: HTMLCanvasElement, o: CodeImageOptions ): void {
  const ctx = canvas.getContext( '2d' );
  if ( !ctx ) { return; }
  const t = o.theme;
  const measured = measureCodeImage( ctx, o );
  const extra = o.backdrop !== 'none' ? Math.max( 0, Math.round( ( o.minHeight || 0 ) - measured.height ) ) : 0;
  const m = { ...measured, height: measured.height + extra };
  canvas.width = Math.round( m.width * o.scale );
  canvas.height = Math.round( m.height * o.scale );
  ctx.setTransform( o.scale, 0, 0, o.scale, 0, 0 );
  ctx.clearRect( 0, 0, m.width, m.height );

  // the backdrop: the theme's two identity colours on a diagonal, the middle of them, or nothing at all
  if ( o.backdrop === 'gradient' ) {
    const g = ctx.createLinearGradient( 0, 0, m.width, m.height );
    // the second identity colour at the top left, the first at the bottom right (owner 2026-10-13: the other way)
    g.addColorStop( 0, t.accent2 === t.accent ? t.window : t.accent2 );
    g.addColorStop( 1, t.accent );
    ctx.fillStyle = g;
    ctx.fillRect( 0, 0, m.width, m.height );
  } else if ( o.backdrop === 'solid' ) {
    // the middle of the gradient (owner 2026-10-13): the two identity colours half and half
    ctx.fillStyle = mix( t.accent, t.accent2 || t.accent );
    ctx.fillRect( 0, 0, m.width, m.height );
  }

  // the editor: a rounded surface in the editor background, lifted off the backdrop by a shadow
  const x = o.padding;
  const y = o.padding + Math.floor( extra / 2 );
  const w = m.width - o.padding * 2;
  const h = measured.height - o.padding * 2;
  ctx.save();
  if ( o.padding > 0 && o.backdrop !== 'none' ) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = 36;
    ctx.shadowOffsetY = 16;
  }
  ctx.fillStyle = t.bg;
  roundRect( ctx, x, y, w, h, 12 );
  ctx.fill();
  ctx.restore();
  // a hairline in the text colour, so the editor keeps its edge on a backdrop close to its own colour
  ctx.strokeStyle = alpha( t.fg, 0.12 );
  ctx.lineWidth = 1;
  roundRect( ctx, x + 0.5, y + 0.5, w - 1, h - 1, 12 );
  ctx.stroke();

  // the bar: three dots in the text colour and the title in the middle
  if ( o.windowBar ) {
    ctx.fillStyle = alpha( t.fg, 0.22 );
    for ( let i = 0; i < 3; i++ ) {
      ctx.beginPath();
      ctx.arc( x + 20 + i * 18, y + 20, 5.5, 0, Math.PI * 2 );
      ctx.fill();
    }
    if ( o.title ) {
      ctx.font = `${ Math.round( o.fontSize * 0.86 ) }px ${ CODE_FONT }`;
      ctx.fillStyle = alpha( t.fg, 0.6 );
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText( o.title, x + w / 2, y + 20, w - 140 );
    }
  }

  // the code
  const inner = 22;
  const top = y + m.bar + inner - ( o.windowBar ? 8 : 0 );
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const colors = t as any;
  o.lines.forEach( ( line, row ) => {
    const baseline = top + row * m.lineHeight + m.lineHeight / 2;
    if ( o.lineNumbers ) {
      ctx.font = `${ o.fontSize }px ${ CODE_FONT }`;
      ctx.fillStyle = alpha( t.lineNumber, 0.75 );
      ctx.textAlign = 'right';
      ctx.fillText( String( row + 1 ), x + inner + m.gutter - m.charWidth * 2, baseline );
      ctx.textAlign = 'left';
    }
    let column = 0;
    for ( const span of line ) {
      ctx.font = `${ span.role === 'comment' ? 'italic ' : '' }${ o.fontSize }px ${ CODE_FONT }`;
      ctx.fillStyle = ( span.role && colors[ span.role ] ) || t.fg;
      // every character sits on the grid of the regular face, so an italic comment or a font
      // without a true monospace bold never pushes the rest of the line off its column
      ctx.fillText( span.text, x + inner + m.gutter + column * m.charWidth, baseline );
      column += span.text.length;
    }
  } );
}
